import React from 'react';
import { IconCheck } from '../../assets/icons/IconCheck';

interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
    label?: string;
    /** 'm' (default) is a 14px label; 'xs' a 12px one, for a checkbox set inside a compact panel. */
    size?: 'm' | 'xs';
}

const SIZE_CLASS: Record<NonNullable<CheckboxProps['size']>, string> = {
    m: 'gap-m text-m',
    xs: 'gap-s text-xs',
};

export const Checkbox: React.FC<CheckboxProps> = ({
    className = '',
    label,
    size = 'm',
    disabled,
    ...props
}) => {
    // Drawn rather than native: a native box takes its fill from accent-color but ignores border-radius.
    // Checked, it takes the primary button's fill, which is off the mirror in dark for a pure white.
    const checkboxClasses = disabled
        ? 'border-primary-500 checked:bg-primary-500 cursor-not-allowed'
        : 'border-primary-700 checked:border-primary-900 checked:bg-primary-900 dark:checked:border-primary-1250 dark:checked:bg-primary-1250 cursor-pointer';

    const labelClasses = disabled
        ? 'text-primary-700 cursor-not-allowed'
        : 'text-content cursor-pointer';

    return (
        <label className={`flex items-start ${SIZE_CLASS[size]} font-medium ${className}`}>
            {/* One line tall, so the box centres on the first line and a wrapped label runs on beneath it. */}
            <span className="relative flex h-lh shrink-0 items-center">
                <input
                    type="checkbox"
                    className={`peer w-[19px] h-[19px] shrink-0 appearance-none rounded-sm border bg-surface touch-target focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-focus-ring ${checkboxClasses}`}
                    disabled={disabled}
                    {...props}
                />
                <IconCheck className="pointer-events-none absolute left-0.5 hidden h-3.75 w-3.75 text-primary-50 peer-checked:block" />
            </span>
            {label && (
                <span className={labelClasses}>
                    {label}
                </span>
            )}
        </label>
    );
};